import { supabase } from '../config/supabase';
import { Photo, PhotoCategory, ResourceType } from '../types/Photo';

const BUCKET = 'media';

interface MediaRow {
  id: string;
  url: string;
  storage_path: string;
  category: string;
  resource_type: ResourceType;
  description: string | null;
  format: string | null;
  duration: number | null;
  created_at: string;
}

const rowToPhoto = (row: MediaRow): Photo => ({
  _id: row.id,
  url: row.url,
  created_at: row.created_at,
  description: row.description || '',
  category: row.category,
  resource_type: row.resource_type,
  storage_path: row.storage_path,
  format: row.format || undefined,
  duration: row.duration || undefined,
});

const getResourceType = (file: File): ResourceType =>
  file.type.startsWith('video/') ? 'video' : 'image';

export const mediaService = {
  // Récupérer tous les médias d'une catégorie (images + vidéos combinées)
  async getByCategory(category: PhotoCategory | string): Promise<Photo[]> {
    const { data, error } = await supabase
      .from('media')
      .select('*')
      .ilike('category', category)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(`Erreur lors de la récupération des médias pour ${category}:`, error);
      throw error;
    }

    return (data || []).map(rowToPhoto);
  },

  // Récupérer tous les médias, toutes catégories confondues
  async getAll(): Promise<Photo[]> {
    const { data, error } = await supabase
      .from('media')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération de tous les médias:', error);
      throw error;
    }

    return (data || []).map(rowToPhoto);
  },

  // Télécharger un ou plusieurs fichiers (upload Storage + insertion DB)
  // Nécessite un utilisateur Supabase authentifié avec le rôle admin (appliqué via RLS)
  async upload(files: FileList | File[], category: PhotoCategory | string, description = ''): Promise<Photo[]> {
    const uploaded: Photo[] = [];
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      const resourceType = getResourceType(file);
      const safeName = file.name.replace(/\s+/g, '_');
      const storagePath = `${category}/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        console.error(`Erreur lors de l'upload de ${file.name}:`, uploadError);
        throw uploadError;
      }

      const { data: publicUrlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);

      const { data: inserted, error: insertError } = await supabase
        .from('media')
        .insert({
          url: publicUrlData.publicUrl,
          storage_path: storagePath,
          category,
          resource_type: resourceType,
          description,
        })
        .select()
        .single();

      if (insertError) {
        console.error(`Erreur lors de l'enregistrement de ${file.name} en base:`, insertError);
        // Nettoyer le fichier orphelin déjà uploadé dans le Storage
        await supabase.storage.from(BUCKET).remove([storagePath]);
        throw insertError;
      }

      uploaded.push(rowToPhoto(inserted as MediaRow));
    }

    return uploaded;
  },

  // Supprimer un média (fichier Storage + ligne DB)
  async remove(id: string): Promise<void> {
    const { data: row, error: fetchError } = await supabase
      .from('media')
      .select('storage_path')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error(`Erreur lors de la récupération du média ${id}:`, fetchError);
      throw fetchError;
    }

    if (row?.storage_path) {
      const { error: storageError } = await supabase.storage.from(BUCKET).remove([row.storage_path]);
      if (storageError) {
        console.error(`Erreur lors de la suppression du fichier ${row.storage_path}:`, storageError);
        // On continue quand même pour supprimer l'entrée en base
      }
    }

    const { error: deleteError } = await supabase.from('media').delete().eq('id', id);
    if (deleteError) {
      console.error(`Erreur lors de la suppression du média ${id} en base:`, deleteError);
      throw deleteError;
    }
  },
};

export default mediaService;
